import type { PageDocument } from '@/lib/db/rxdb/hooks/entities/pages'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'

type Ranked = Pick<ProjectDocument, 'sortOrder' | 'createdOn'>

/** Projects in their sidebar order: ranked first, then oldest first. */
export function sortByRank<T extends Ranked>(items: ReadonlyArray<T>): Array<T> {
  return [...items].sort((a, b) => {
    if (a.sortOrder && b.sortOrder) return a.sortOrder.localeCompare(b.sortOrder)
    if (a.sortOrder) return -1
    if (b.sortOrder) return 1
    return a.createdOn - b.createdOn
  })
}

export interface DocLocationGroup {
  kind: 'space' | 'project'
  location: ProjectDocument
  /** Every live doc that lives here, at any depth. */
  pages: Array<PageDocument>
}

/**
 * A space's docs grouped by where each one lives (design 22a): the space's own
 * docs first, then one group per project that has docs, in sidebar order.
 */
export function groupDocsByLocation(
  space: ProjectDocument,
  projects: ReadonlyArray<ProjectDocument>,
  pages: ReadonlyArray<PageDocument>,
): Array<DocLocationGroup> {
  const byProject = new Map<string, Array<PageDocument>>()
  for (const page of pages) {
    if (page.isDeleted) continue
    const list = byProject.get(page.projectId) ?? []
    list.push(page)
    byProject.set(page.projectId, list)
  }

  const groups: Array<DocLocationGroup> = [
    { kind: 'space', location: space, pages: byProject.get(space.id) ?? [] },
  ]
  for (const project of sortByRank(projects.filter((p) => !p.isDeleted && p.parentProjectId === space.id))) {
    const docs = byProject.get(project.id)
    if (docs && docs.length > 0) groups.push({ kind: 'project', location: project, pages: docs })
  }
  return groups
}

/** The space's and its projects' docs, most recently edited first. */
export function recentDocs(
  locationIds: ReadonlySet<string>,
  pages: ReadonlyArray<PageDocument>,
): Array<PageDocument> {
  return pages
    .filter((p) => !p.isDeleted && locationIds.has(p.projectId))
    .sort((a, b) => b.updatedOn - a.updatedOn)
}
